/* eslint-disable react-hooks/exhaustive-deps */
import { Search } from '@/components/search'
import { BracketPaymentLoading } from '@/components/skeleton'
import { LIMIT, OFFSET } from '@/constants/Common'
import { useDebounce } from '@/hooks/useDebounce'
import { useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { useGetBracketPayments } from '@/services'
import { BracketPayment } from '@/types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { BracketPaymentItem } from './BracketPaymentItem'

type Props = {
  leagueId: number
  currency?: string
}

export const BracketPayments = ({ leagueId, currency }: Props) => {
  const { t } = useTranslation()

  const params = useParams()
  const { token } = useAppSelector(authSelector)

  const { bracketId } = params

  const [searchText, setSearchText] = useState('')
  const [offset, setOffset] = useState(OFFSET)
  const [payments, setPayments] = useState<Map<number, BracketPayment>>(new Map())
  const [lastElement, setLastElement] = useState<any>(null)

  const debouncedSearchText = useDebounce<string>(searchText || '*', 200)

  const { data: resPayments, isLoading } = useGetBracketPayments(
    {
      limit: LIMIT,
      offset: offset,
      bracketId: Number(bracketId),
      query: debouncedSearchText || '*',
      leagueId: leagueId,
    },
    token
  )

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        setOffset((pre) => pre + LIMIT)
      }
    })
  )

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
    setOffset(OFFSET)
  }

  useEffect(() => {
    if (resPayments) {
      setPayments((old) => {
        let temp: Map<number, BracketPayment>
        if (offset === OFFSET) {
          temp = new Map()
        } else {
          temp = old
        }

        resPayments.result.hits.forEach((mem) => {
          temp.set(mem.id, mem)
        })

        return temp
      })
    }
  }, [resPayments])

  useEffect(() => {
    const currentElement = lastElement
    const currentObserver = observer.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [lastElement])

  return (
    <div className="flex flex-col gap-y-4">
      <Search searchText={searchText} onChangeInput={onChangeInput} className="py-2 px-3 w-56 mt-3 gap-x-2" />
      {Array.from(payments.values())?.map((item, index) => {
        return index === payments.size - 1 && resPayments?.result.hasMore ? (
          <div key={item.id} ref={setLastElement}>
            <BracketPaymentItem key={item.id} bracketPayment={item} currency={currency} />
          </div>
        ) : (
          <BracketPaymentItem key={item.id} bracketPayment={item} currency={currency} />
        )
      })}

      {isLoading && (
        <>
          <BracketPaymentLoading />
          <BracketPaymentLoading />
          <BracketPaymentLoading />
          <BracketPaymentLoading />
          <BracketPaymentLoading />
          <BracketPaymentLoading />
        </>
      )}

      {resPayments && !resPayments?.result.hasMore && (
        <div className="flex justify-center">
          <span className="text-white font-semibold text-opacity-60">{t('common.noMoreResults')}</span>
        </div>
      )}
    </div>
  )
}
